import { Table } from 'antd'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { BpPagination, IBpPaginationProps } from '../pagination/BpPagination'
import { TableProps } from 'antd/lib/table/Table'
import { TTableResponse } from '../../../types/TableResponse.type'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { usePreviousValue } from '../../../lib/hooks/UsePreviousValue'
import { useTranslation } from 'react-i18next'
import { NoResultsStub } from '../../../../lib/widgets/stubs/NoResultsStub'
import { StubLayout } from '../../../../lib/widgets/stubs/StubLayout'
import {
  AppliedFiltersBoundary,
  useAppliedFilters,
} from '../../../../lib/context/applied-filters/AppliedFiltersProvider'
import { useMenuLayout } from '../../../../app/layouts/main-layout/hooks/UseMenuLayout'
import { useAiButtonSlot } from '../../ai/ai-provider/AiProvider'
import {
  STICKY_EXPANDED_ROW_CLASS,
  STICKY_EXPANDED_ROWS_CLASS,
  stickyExpandedRowsStyle,
} from '../../../../lib/ui/table/lib/stickyExpandedRow'

const ClickableCell = (props: any) => {
  const { children, ...restProps } = props

  return (
    <td {...restProps}>
      <div
        className={restProps.colSpan ? '' : 'h-fit w-fit cursor-auto'}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </td>
  )
}

export interface IBpTableProps<RecordType> extends TableProps<RecordType> {
  data?: TTableResponse<RecordType>
  /** Заглушка пустого списка: рисуется вместо таблицы. */
  emptyStub?: ReactNode
  /** Отступ раскладки между контентом над таблицей и ней самой — его заглушка вычитает из своего. */
  stubGap?: number
  loading?: boolean
  onChange?: (
    pagination: IBpPaginationProps,
    filters?: Record<string, FilterValue | null>,
    sorter?: SorterResult<RecordType> | SorterResult<RecordType>[],
    extra?: TableCurrentDataSource<RecordType>,
  ) => void
  onClickRow?: (record: any) => void
  hidePagination?: boolean
  sizePagination?: 'sm' | 'md'
  bottomSection?: ReactNode
  /** Раскрытая строка стоит на месте при горизонтальной прокрутке и не заходит на закреплённые справа колонки. */
  stickyExpandedRow?: boolean
}

export const BpTable = <T extends object>(props: IBpTableProps<T>) => {
  const { t } = useTranslation()
  const {
    data,
    emptyStub,
    stubGap,
    loading,
    onChange,
    onClickRow,
    pagination,
    scroll,
    bottomSection,
    sizePagination,
    stickyExpandedRow,
    ...tableProps
  } = props
  const { menuLayout } = useMenuLayout()
  const aiButtonSlot = useAiButtonSlot(menuLayout === 'sidebar')

  const [bpPagination, setBpPagination] = useState<IBpPaginationProps>({
    current: pagination ? pagination.current : 1,
    pageSize: pagination ? pagination.pageSize : 10,
    total: pagination ? pagination.total : 1,
    totalRows: 1,
  })
  const [bpSorter, setBpSorter] = useState<SorterResult<T> | SorterResult<T>[]>()
  const [bpFilters, setBpFilters] = useState<Record<string, FilterValue | null>>()
  const previousBpPagination = usePreviousValue(bpPagination)

  useEffect(() => {
    if (!data?.data) return
    const { pageCount, total } = data.data
    setBpPagination({ ...bpPagination, totalRows: total, total: pageCount })
  }, [data])

  useEffect(() => {
    if (onChange && bpPagination.current && bpPagination.current > 0)
      onChange(bpPagination, bpFilters, bpSorter)
  }, [bpPagination])

  useEffect(() => {
    const newPagination = {
      ...bpPagination,
      ...pagination,
    }

    if (
      previousBpPagination &&
      (previousBpPagination as IBpPaginationProps).current !== newPagination.current
    ) {
      setBpPagination(newPagination)
    }
  }, [pagination])

  const { hasApplied: hasAppliedFilters } = useAppliedFilters()

  // при применённых фильтрах вместо zero state показываем "ничего не найдено"
  const stub = hasAppliedFilters ? <NoResultsStub /> : emptyStub

  const expandedRowRender = tableProps.expandable?.expandedRowRender
  const expandable = expandedRowRender
    ? {
        ...tableProps.expandable,
        expandedRowRender: (...args: Parameters<typeof expandedRowRender>) => (
          <AppliedFiltersBoundary>{expandedRowRender(...args)}</AppliedFiltersBoundary>
        ),
        ...(stickyExpandedRow && { expandedRowClassName: () => STICKY_EXPANDED_ROW_CLASS }),
      }
    : tableProps.expandable

  const components = useMemo(
    () => ({
      ...tableProps.components,
      body: {
        ...tableProps.components?.body,
        ...(!!onClickRow && { cell: ClickableCell }),
      },
    }),
    [tableProps.components, onClickRow],
  )

  // пустой список — заглушка вместо таблицы; пока грузится, остаётся таблица с лоадером
  if (stub && !loading && !tableProps.dataSource?.length)
    return <StubLayout gap={stubGap}>{stub}</StubLayout>

  return (
    <div className={'grid h-full max-w-full grid-cols-1 grid-rows-[1fr_auto] items-end'}>
      <Table
        {...tableProps}
        className={`h-full ${!!onClickRow && 'expandable'} ${
          stickyExpandedRow ? STICKY_EXPANDED_ROWS_CLASS : ''
        }`}
        locale={{
          // пока грузится, пустое место вместо дефолтного antd «No data»
          ...(loading && { emptyText: <span /> }),
          triggerAsc: t('table.clickToSortAscending'),
          triggerDesc: t('table.clickToSortDescending'),
          cancelSort: t('table.clickToCancelSorting'),
        }}
        style={
          stickyExpandedRow
            ? stickyExpandedRowsStyle(tableProps.columns, tableProps.style)
            : tableProps.style
        }
        components={components}
        expandable={expandable}
        loading={loading}
        pagination={false}
        scroll={scroll ?? { x: '1344px' }}
        showSorterTooltip={false}
        tableLayout={'auto'}
        onChange={(pagination, filters, sorter, extra) => {
          setBpSorter(sorter)
          setBpFilters(filters)
          if (onChange) onChange(bpPagination, filters, sorter, extra)
        }}
        onRow={(record) => ({
          onClick: (e) => {
            const selection = window.getSelection()
            const hasSelection = selection && selection.toString().length > 0

            if (!hasSelection && onClickRow) {
              onClickRow(record)
            }
          },
        })}
      />
      {!props.hidePagination && (
        <div className='relative bg-white'>
          {bottomSection && (
            <div className='border-t-[2px] border-focus/[0.03]'>{bottomSection}</div>
          )}
          <div
            {...aiButtonSlot}
            data-table-pagination
            className={
              'flex min-h-[73px] w-full items-center border-t-[1px] border-[#8D7CA7]/[0.12] py-[14px]'
            }
          >
            {!!tableProps.dataSource?.length && (
              <BpPagination
                {...bpPagination}
                disabled={loading}
                sizePagination={sizePagination}
                onChange={(page, pageSize) => {
                  setBpPagination({
                    ...bpPagination,
                    current: page,
                    pageSize,
                  })
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
